'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    CostCenterItem,
    CreateCostCenterData,
    OrganizationUnit,
    UpdateCostCenterData,
    createCostCenter,
    deleteCostCenter,
    getCostCenterChildren,
    getCostCenterNextCode,
    getCostCenterTree,
    getOrganizationUnits,
    searchCostCenters,
    updateCostCenter,
} from '@/lib/api/financial/cost-center-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
    MdAccountTree,
    MdAdd,
    MdBusiness,
    MdChevronRight,
    MdClose,
    MdDelete,
    MdEdit,
    MdExpandMore,
    MdSearch,
} from 'react-icons/md'
import { toast } from 'react-toastify'

interface CostCenterForm {
    name: string
    code: string
    parentId: number | null
    active: boolean
}

const initialForm: CostCenterForm = {
    name: '',
    code: '',
    parentId: null,
    active: true,
}

function TreeNode({
    item,
    level,
    expandedIds,
    childrenMap,
    loadingIds,
    onToggleExpand,
    onEdit,
    onDelete,
    onAddChild,
    canUpdate,
    canDelete,
    canCreate,
}: {
    item: CostCenterItem
    level: number
    expandedIds: Set<number>
    childrenMap: Map<number, CostCenterItem[]>
    loadingIds: Set<number>
    onToggleExpand: (item: CostCenterItem) => void
    onEdit: (item: CostCenterItem) => void
    onDelete: (id: number) => void
    onAddChild: (parent: CostCenterItem) => void
    canUpdate: boolean
    canDelete: boolean
    canCreate: boolean
}) {
    const childCount = item._count?.children || 0
    const isExpanded = expandedIds.has(item.id)
    const isLoading = loadingIds.has(item.id)
    const children = childrenMap.get(item.id)

    return (
        <div>
            <div
                className={`flex items-center gap-2 py-2 px-3 hover:bg-base-200/50 rounded-lg group transition-colors ${level === 0 ? 'bg-base-200/30' : ''}`}
                style={{ paddingLeft: `${level * 24 + 12}px` }}
            >
                <button
                    className={`btn btn-ghost btn-xs btn-square ${childCount === 0 ? 'invisible' : ''}`}
                    onClick={() => onToggleExpand(item)}
                >
                    {isLoading ? (
                        <span className="loading loading-spinner loading-xs"></span>
                    ) : isExpanded ? (
                        <MdExpandMore size={18} />
                    ) : (
                        <MdChevronRight size={18} />
                    )}
                </button>

                <span className={`flex-1 text-sm ${level === 0 ? 'font-bold' : ''}`}>
                    {item.name}
                </span>

                <span className="badge badge-sm badge-outline font-mono">
                    {item.code}
                </span>

                {!item.active && (
                    <span className="badge badge-xs badge-ghost">Inativo</span>
                )}

                {childCount > 0 && (
                    <span className="badge badge-xs badge-ghost">{childCount}</span>
                )}

                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {canCreate && (
                        <button
                            className="btn btn-ghost btn-xs"
                            onClick={() => onAddChild(item)}
                            title="Adicionar subcentro"
                        >
                            <MdAdd size={14} />
                        </button>
                    )}
                    {canUpdate && (
                        <button
                            className="btn btn-ghost btn-xs"
                            onClick={() => onEdit(item)}
                            title="Editar"
                        >
                            <MdEdit size={14} />
                        </button>
                    )}
                    {canDelete && childCount === 0 && (
                        <button
                            className="btn btn-ghost btn-xs text-error"
                            onClick={() => onDelete(item.id)}
                            title="Excluir"
                        >
                            <MdDelete size={14} />
                        </button>
                    )}
                </div>
            </div>

            {isExpanded && children && (
                <div>
                    {children.map(child => (
                        <TreeNode
                            key={child.id}
                            item={child}
                            level={level + 1}
                            expandedIds={expandedIds}
                            childrenMap={childrenMap}
                            loadingIds={loadingIds}
                            onToggleExpand={onToggleExpand}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            onAddChild={onAddChild}
                            canUpdate={canUpdate}
                            canDelete={canDelete}
                            canCreate={canCreate}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

export default function CostCentersPage() {
    const { user } = useAuth()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions('financial', 'cost_centers')

    const [rootItems, setRootItems] = useState<CostCenterItem[]>([])
    const [loading, setLoading] = useState(true)

    // Lazy loading
    const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
    const [childrenMap, setChildrenMap] = useState<Map<number, CostCenterItem[]>>(new Map())
    const [loadingIds, setLoadingIds] = useState<Set<number>>(new Set())

    // Busca
    const [searchInput, setSearchInput] = useState('')
    const [searchResults, setSearchResults] = useState<CostCenterItem[] | null>(null)
    const [searching, setSearching] = useState(false)
    const searchTimeout = useRef<NodeJS.Timeout>(null)

    // Modal
    const modalRef = useRef<HTMLDialogElement>(null)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [form, setForm] = useState<CostCenterForm>(initialForm)
    const [loadingCode, setLoadingCode] = useState(false)
    const [saving, setSaving] = useState(false)

    // Delete
    const [deleteId, setDeleteId] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)

    // Parent select
    const [allLoadedItems, setAllLoadedItems] = useState<CostCenterItem[]>([])

    // Unidades da organização
    const [orgUnits, setOrgUnits] = useState<OrganizationUnit[]>([])
    const [loadingUnits, setLoadingUnits] = useState(false)

    useEffect(() => {
        loadRoots()
        loadOrgUnits()
    }, [])

    useEffect(() => {
        const flat: CostCenterItem[] = [...rootItems]
        for (const [, children] of childrenMap) {
            flat.push(...children)
        }
        setAllLoadedItems(flat)
    }, [rootItems, childrenMap])

    const loadRoots = async () => {
        setLoading(true)
        setExpandedIds(new Set())
        setChildrenMap(new Map())
        try {
            const res = await getCostCenterTree()
            if (res.success && res.result) {
                setRootItems(res.result)
            }
        } catch {
            toast.error('Erro ao carregar centros de custo')
        } finally {
            setLoading(false)
        }
    }

    const loadOrgUnits = async () => {
        setLoadingUnits(true)
        try {
            const res = await getOrganizationUnits()
            if (res.success && res.result) {
                setOrgUnits(res.result)
            }
        } catch {
            // silencioso
        } finally {
            setLoadingUnits(false)
        }
    }

    const handleToggleExpand = useCallback(async (item: CostCenterItem) => {
        const id = item.id

        if (expandedIds.has(id)) {
            setExpandedIds(prev => {
                const next = new Set(prev)
                next.delete(id)
                return next
            })
            return
        }

        if (childrenMap.has(id)) {
            setExpandedIds(prev => new Set(prev).add(id))
            return
        }

        setLoadingIds(prev => new Set(prev).add(id))
        try {
            const res = await getCostCenterChildren(id)
            if (res.success && res.result) {
                setChildrenMap(prev => {
                    const next = new Map(prev)
                    next.set(id, res.result!)
                    return next
                })
                setExpandedIds(prev => new Set(prev).add(id))
            }
        } catch {
            toast.error('Erro ao carregar subcentros')
        } finally {
            setLoadingIds(prev => {
                const next = new Set(prev)
                next.delete(id)
                return next
            })
        }
    }, [expandedIds, childrenMap])

    // Busca com debounce
    useEffect(() => {
        if (searchTimeout.current) clearTimeout(searchTimeout.current)

        if (!searchInput.trim()) {
            setSearchResults(null)
            return
        }

        searchTimeout.current = setTimeout(async () => {
            setSearching(true)
            try {
                const res = await searchCostCenters(searchInput.trim())
                if (res.success && res.result) {
                    setSearchResults(res.result)
                }
            } catch { /* ignore */ }
            finally { setSearching(false) }
        }, 300)

        return () => {
            if (searchTimeout.current) clearTimeout(searchTimeout.current)
        }
    }, [searchInput])

    const fetchNextCode = async (parentId?: number | null) => {
        setLoadingCode(true)
        try {
            const res = await getCostCenterNextCode(parentId)
            if (res.success && res.result) {
                setForm(prev => ({ ...prev, code: res.result!.code }))
            }
        } catch { /* ignore */ }
        finally { setLoadingCode(false) }
    }

    const openCreateModal = () => {
        setEditingId(null)
        setForm(initialForm)
        modalRef.current?.showModal()
        fetchNextCode(null)
    }

    const openCreateChildModal = (parent: CostCenterItem) => {
        setEditingId(null)
        setForm({ ...initialForm, parentId: parent.id, code: '' })
        modalRef.current?.showModal()
        fetchNextCode(parent.id)
    }

    const openEditModal = (item: CostCenterItem) => {
        setEditingId(item.id)
        setForm({
            name: item.name,
            code: item.code,
            parentId: item.parentId,
            active: item.active,
        })
        modalRef.current?.showModal()
    }

    const closeModal = () => {
        modalRef.current?.close()
        setEditingId(null)
        setForm(initialForm)
    }

    const handleParentChange = (parentId: number | null) => {
        setForm(prev => ({ ...prev, parentId }))
        if (!editingId) fetchNextCode(parentId)
    }

    const handleSave = async () => {
        if (!form.name.trim()) {
            toast.warning('Informe o nome do centro de custo')
            return
        }

        setSaving(true)
        try {
            if (editingId) {
                const data: UpdateCostCenterData = {
                    name: form.name.trim(),
                    code: form.code.trim() || undefined,
                    parentId: form.parentId,
                    active: form.active,
                }
                const res = await updateCostCenter(editingId, data)
                if (res.success) {
                    toast.success('Centro de custo atualizado com sucesso')
                    closeModal()
                    loadRoots()
                } else {
                    toast.error(res.error || 'Erro ao atualizar centro de custo')
                }
            } else {
                const data: CreateCostCenterData = {
                    name: form.name.trim(),
                    code: form.code.trim() || undefined,
                    parentId: form.parentId,
                }
                const res = await createCostCenter(data)
                if (res.success) {
                    toast.success('Centro de custo criado com sucesso')
                    closeModal()
                    loadRoots()
                } else {
                    toast.error(res.error || 'Erro ao criar centro de custo')
                }
            }
        } catch {
            toast.error('Erro ao salvar centro de custo')
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async () => {
        if (!deleteId) return
        setDeleting(true)
        try {
            const res = await deleteCostCenter(deleteId)
            if (res.success) {
                toast.success('Centro de custo excluído com sucesso')
                setDeleteId(null)
                loadRoots()
            } else {
                toast.error(res.error || 'Erro ao excluir centro de custo')
            }
        } catch {
            toast.error('Erro ao excluir centro de custo')
        } finally {
            setDeleting(false)
        }
    }

    const totalItems = rootItems.length

    return (
        <div className="p-4 md:p-6 max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <MdAccountTree size={28} className="text-secondary" />
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="text-2xl font-bold">Centros de Custo</h1>
                            {totalItems > 0 && (
                                <span className="badge badge-secondary badge-sm">
                                    {totalItems}
                                </span>
                            )}
                        </div>
                        <p className="text-base-content/60 text-sm mt-1">
                            Gerencie a estrutura hierárquica de centros de custo
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <button className="btn btn-secondary" onClick={openCreateModal}>
                        <MdAdd size={20} />
                        Novo Centro de Custo
                    </button>
                )}
            </div>

            {/* Search */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="relative flex-1 max-w-sm">
                    <MdSearch
                        size={18}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40 z-10"
                    />
                    <input
                        type="text"
                        className="input input-bordered input-sm w-full pl-9"
                        placeholder="Buscar por nome ou código..."
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                    />
                    {searching && (
                        <span className="loading loading-spinner loading-xs absolute right-3 top-1/2 -translate-y-1/2"></span>
                    )}
                    {searchInput && !searching && (
                        <button
                            className="btn btn-ghost btn-xs btn-circle absolute right-2 top-1/2 -translate-y-1/2"
                            onClick={() => setSearchInput('')}
                        >
                            <MdClose size={14} />
                        </button>
                    )}
                </div>

                {totalItems > 0 && !searchResults && (
                    <span className="text-sm text-base-content/50 ml-auto">
                        {totalItems} centro{totalItems !== 1 ? 's' : ''} raiz
                    </span>
                )}
            </div>

            {/* Loading */}
            {loading && (
                <div className="flex items-center justify-center min-h-[300px]">
                    <div className="loading loading-spinner loading-lg text-secondary"></div>
                </div>
            )}

            {/* Empty */}
            {!loading && rootItems.length === 0 && !searchInput && (
                <div className="card bg-base-100 shadow">
                    <div className="card-body items-center text-center py-16">
                        <MdAccountTree size={48} className="text-base-content/30" />
                        <p className="text-base-content/60 mt-2">Nenhum centro de custo cadastrado</p>
                        {canCreate && (
                            <button className="btn btn-secondary btn-sm mt-4" onClick={openCreateModal}>
                                <MdAdd size={18} />
                                Cadastrar Centro de Custo
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Search results */}
            {searchResults && (
                <div className="card bg-base-100 shadow">
                    <div className="card-body p-4">
                        <p className="text-sm text-base-content/60 mb-3">
                            {searchResults.length} resultado{searchResults.length !== 1 ? 's' : ''} para "{searchInput}"
                        </p>
                        {searchResults.length === 0 && (
                            <p className="text-center text-base-content/40 py-8">Nenhum centro de custo encontrado</p>
                        )}
                        {searchResults.map(item => (
                            <div
                                key={item.id}
                                className="flex items-center gap-3 py-2 px-3 hover:bg-base-200/50 rounded-lg group"
                            >
                                <span className="flex-1 text-sm">{item.name}</span>
                                {item.parent && (
                                    <span className="text-xs text-base-content/40">
                                        Pai: {item.parent.code} - {item.parent.name}
                                    </span>
                                )}
                                <span className="badge badge-sm badge-outline font-mono">
                                    {item.code}
                                </span>
                                {!item.active && (
                                    <span className="badge badge-xs badge-ghost">Inativo</span>
                                )}
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {canUpdate && (
                                        <button className="btn btn-ghost btn-xs" onClick={() => openEditModal(item)}>
                                            <MdEdit size={14} />
                                        </button>
                                    )}
                                    {canDelete && (
                                        <button className="btn btn-ghost btn-xs text-error" onClick={() => setDeleteId(item.id)}>
                                            <MdDelete size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Tree view */}
            {!loading && rootItems.length > 0 && !searchResults && (
                <div className="card bg-base-100 shadow">
                    <div className="card-body p-4">
                        {rootItems.map(item => (
                            <TreeNode
                                key={item.id}
                                item={item}
                                level={0}
                                expandedIds={expandedIds}
                                childrenMap={childrenMap}
                                loadingIds={loadingIds}
                                onToggleExpand={handleToggleExpand}
                                onEdit={openEditModal}
                                onDelete={setDeleteId}
                                onAddChild={openCreateChildModal}
                                canUpdate={canUpdate}
                                canDelete={canDelete}
                                canCreate={canCreate}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Unidades da Organização */}
            {orgUnits.length > 0 && (
                <div className="card bg-base-100 shadow mt-6">
                    <div className="card-body p-4">
                        <h2 className="text-lg font-semibold flex items-center gap-2 mb-3">
                            <MdBusiness size={20} className="text-secondary" />
                            Unidades da Organização
                        </h2>
                        <p className="text-sm text-base-content/60 mb-4">
                            Unidades disponíveis que podem ser utilizadas como referência para centros de custo.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                            {orgUnits.map(unit => (
                                <div
                                    key={unit.id}
                                    className="flex items-center gap-3 p-3 bg-base-200/40 rounded-lg"
                                >
                                    <MdBusiness size={18} className="text-base-content/40 shrink-0" />
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium truncate">
                                            {unit.tradingName || unit.name}
                                        </p>
                                        {unit.tradingName && (
                                            <p className="text-xs text-base-content/50 truncate">
                                                {unit.name}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {loadingUnits && (
                <div className="flex items-center justify-center py-8">
                    <span className="loading loading-spinner loading-sm text-base-content/40"></span>
                </div>
            )}

            {/* Create/Edit Modal */}
            <dialog ref={modalRef} className="modal">
                <div className="modal-box !max-w-lg">
                    <h3 className="font-bold text-lg mb-4">
                        {editingId ? 'Editar Centro de Custo' : 'Novo Centro de Custo'}
                    </h3>

                    <div className="space-y-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Nome *</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Ex: Administrativo, Comercial, TI..."
                                value={form.name}
                                onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Código</span>
                            </label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    className="input input-bordered w-full font-mono"
                                    placeholder="Gerado automaticamente"
                                    value={form.code}
                                    onChange={e => setForm(prev => ({ ...prev, code: e.target.value }))}
                                />
                                {loadingCode && (
                                    <span className="loading loading-spinner loading-sm text-secondary"></span>
                                )}
                            </div>
                            <label className="label">
                                <span className="label-text-alt text-base-content/50">
                                    Deixe em branco para gerar automaticamente
                                </span>
                            </label>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Centro Pai</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={form.parentId || ''}
                                onChange={e =>
                                    handleParentChange(
                                        e.target.value ? parseInt(e.target.value, 10) : null,
                                    )
                                }
                            >
                                <option value="">Nenhum (centro raiz)</option>
                                {allLoadedItems
                                    .filter(a => a.active && a.id !== editingId)
                                    .map(a => (
                                        <option key={a.id} value={a.id}>
                                            {a.code} - {a.name}
                                        </option>
                                    ))}
                            </select>
                        </div>

                        {editingId && (
                            <div className="form-control">
                                <label className="label cursor-pointer justify-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="toggle toggle-secondary toggle-sm"
                                        checked={form.active}
                                        onChange={e =>
                                            setForm(prev => ({ ...prev, active: e.target.checked }))
                                        }
                                    />
                                    <span className="label-text">Ativo</span>
                                </label>
                            </div>
                        )}
                    </div>

                    <div className="modal-action">
                        <button className="btn btn-ghost" onClick={closeModal} disabled={saving}>
                            Cancelar
                        </button>
                        <button
                            className="btn btn-secondary"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving && (
                                <span className="loading loading-spinner loading-sm"></span>
                            )}
                            {editingId ? 'Salvar' : 'Criar'}
                        </button>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button onClick={closeModal}>close</button>
                </form>
            </dialog>

            {/* Delete confirmation */}
            <ConfirmModal
                isOpen={!!deleteId}
                onClose={() => setDeleteId(null)}
                onConfirm={handleDelete}
                title="Exclusão de Centro de Custo"
                message="Tem certeza de que deseja excluir este centro de custo? Esta ação não pode ser desfeita."
                loading={deleting}
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
            />
        </div>
    )
}
